import {BaseModel} from '../base/BaseModel';
import {Prototype} from '../../decorators/Prototype';
import FEATURES_CONFIG from './app-features-config.json';

const TOKEN_FEATURES_KEY = 'features';
const QA_FEATURES_STORAGE_KEY = 'FE_FEATURES';

/**
 * @class AppFeaturesModel
 * @extends BaseModel
 *
 * @property {object} attributes.defaultFeatures    - Default features config
 * @property {object} attributes.regionalFeatures   - Features config for specific countries
 * @property {object} attributes.tokenFeatures      - Features for specific user through token (A/B experiments)
 * @property {object} attributes.qaFeatures         - Features enabled/disabled for QA
 */
@Prototype({
    defaults: {
        defaultFeatures: FEATURES_CONFIG.BASE,
        regionalFeatures: _.omit(FEATURES_CONFIG, 'BASE'),
        tokenFeatures: {},
        qaFeatures: {},
        tokenFeaturesLoaded: false
    }
})
export class AppFeaturesModel extends BaseModel {

    initialize (data, {CURRENT_USER}) {
        this.currentUser = CURRENT_USER;
        CURRENT_USER.appFeatures = this; // setting back link to avoid webpack circular dependency

        if (this.currentUser && this.currentUser.get('token')) {
            this.loadTokenFeatures();
            this.listenTo(this.currentUser.get('token'), 'change', this.loadTokenFeatures);
            this.listenTo(this.currentUser, 'change:token', () => {
                this.loadTokenFeatures();
                this.stopListening(this.loadTokenFeatures);
                this.listenTo(this.currentUser.get('token'), 'change', this.loadTokenFeatures);
            });
        } else {
            console.warn('AppFeaturesModel expects CURRENT_USER to be set and have a Token model.')
        }
        this.loadQAFeatures();
    }

    renew () {
        this.clear();
        this.set(_.extend({}, this.defaults));
        this.loadTokenFeatures();
        this.loadQAFeatures();
    }

    /**
     * Get default config
     *
     * @returns {object}
     */
    getDefaultFeatures () {
        return this.get('defaultFeatures') || {};
    }

    /**
     * Get default config value for feature
     *
     * @param {string} feature
     * @returns {boolean|undefined}
     */
    getDefaultFeature ({feature}) {
        return this.getDefaultFeatures()[feature];
    }

    /**
     * Get regional config
     * If no region present, return empty config
     *
     * @param {{region: {string}}}
     * @returns {object}
     */
    getRegionalFeatures ({region}) {
        if (!region) {
            return {};
        }
        const regionalFeatures = this.get('regionalFeatures');

        region = region.toUpperCase();
        if (!regionalFeatures || !regionalFeatures[region]) {
            return {};
        }
        return regionalFeatures[region];
    }

    /**
     * Get region specific config value for feature
     * if no region present, return "Enabled"
     *
     * @param {{feature: {string}, country: {string}}}
     * @returns {boolean|undefined}
     */
    getRegionalFeature ({feature, country}) {
        if (!country) {
            return true;
        }
        country = country.toUpperCase();
        return this.getRegionalFeatures({region: country})[feature];
    }


    /**
     * Try to get token features, do nothing if not loaded yet
     */
    loadTokenFeatures () {
        const tokenModel = this.currentUser.get('token');
        if (tokenModel && tokenModel.get('decoded')) {
            this.setTokenFeaturesFromModel({tokenModel});
        } else {
            // just waiting for token to load
        }
    }

    /**
     *  Set features from BE token
     *
     *  @param {{tokenModel: {TokenModel}}}
     */
    setTokenFeaturesFromModel ({tokenModel}) {
        let tokenFeatures = {};

        let decoded = tokenModel.get('decoded');
        if (decoded) {
            if (decoded[TOKEN_FEATURES_KEY]) {
                tokenFeatures = this.parseTokenFeatures(decoded[TOKEN_FEATURES_KEY]);

                // @TODO: Check this before KWS_CORN prod release
                if (tokenFeatures && tokenFeatures['KWS_CORN']) {
                    delete tokenFeatures['KWS_CORN'];
                }
            }
            this.set({
                tokenFeatures,
                tokenFeaturesLoaded: true
            });
        }
    }

    /**
     * Parse fmethod for token features
     *
     * @param {object} rawTokenFeatures
     * @returns {object}
     */
    parseTokenFeatures (rawTokenFeatures) {

        return rawTokenFeatures.reduce((result, item) => {
            return _.extend(result, {[item]: true});
        }, {});
    }

    /**
     * @returns {object}
     */
    getTokenFeatures () {
        return this.get('tokenFeatures') || {};
    }

    /**
     * @param {feature: {string}}
     * @returns {boolean|undefined}
     */
    getTokenFeature({feature}) {
        return this.getTokenFeatures()[feature];
    }


    /**
     * @returns {object}
     */
    getQAFeatures() {
        return this.get('qaFeatures') || {};
    }

    /**
     * @param {feature: {string}}
     * @returns {boolean|undefined}
     */
    getQAFeature({feature}) {
        return this.getQAFeatures()[feature];
    }

    /**
     * @param {object} features
     */
    setQAFeatures({qaFeatures} = {}) {
        this.set({qaFeatures});
        this.saveQAFeatures();
    }

    /**
     * Set QA setting to local storage to override
     * Override will be saved only if it differs from result of other configs
     *
     * @param {{feature: {string}, value: {boolean}, country: {string}}}
     */
    setQAFeature({feature, value, country}) {
        const prodValue = this.isFeatureEnabledWithoutQA({feature, country});

        if (prodValue === value) {
            this.removeQAFeature({feature});
        } else {
            let qaFeatures = _.extend(this.getQAFeatures(), {[feature]: value});

            this.setQAFeatures({qaFeatures});
        }
    }

    /**
     * @param {{feature: {string}, country: {string}}}
     * @returns {boolean|undefined}
     */
    toggleQAFeature({feature, country}) {
        const value = !this.isFeatureEnabled({feature, country});

        this.setQAFeature({feature, value, country});
    }

    /**
     * Clean single QA override
     *
     * @param {{feature: {string}}}
     */
    removeQAFeature({feature}) {
        const qaFeatures = _.omit(this.get('qaFeatures'), feature);

        this.setQAFeatures({qaFeatures});
    }

    /**
     * Clean all QA overrides
     */
    resetQAFeatures() {
        this.setQAFeatures({qaFeatures: {}});
    }

    /**
     * Save QA features to localStorage
     */
    saveQAFeatures() {
        localStorage.setItem(QA_FEATURES_STORAGE_KEY, JSON.stringify(this.getQAFeatures()));
    }

    /**
     * Load QA features to localStorage
     *
     * @returns {object}
     */
    loadQAFeatures() {
        const qaFeatures = JSON.parse(localStorage.getItem(QA_FEATURES_STORAGE_KEY)) || {};

        this.setQAFeatures({qaFeatures});
    }


    /**
     * Check if feature is enabled independent of QA Features config
     *
     * @param {{feature: {string}, country: {string}}}
     * @returns {boolean}
     */
    isFeatureEnabledWithoutQA({feature, country}) {
        if (!feature) {
            console.warn('AppFeaturesModel expects feature to be provided');
        }
        if (!country) {
            console.warn('AppFeaturesModel expects country to be provided');
        }

        const defaultValue = this.getDefaultFeature({feature});
        const regionalValue = this.getRegionalFeature({feature, country});
        const tokenValue = this.getTokenFeature({feature});

        if (!_.isNil(tokenValue)) {
            return tokenValue;
        } else if (!_.isNil(regionalValue)) {
            return regionalValue;
        } else if (!_.isNil(defaultValue)) {
            return defaultValue;
        }

        return false; // say "enabled" for unknown features
    }

    /**
     * Check if feature is enabled
     *
     * @param {{feature: {string}, country: {string}}}
     * @returns {boolean}
     */
    isFeatureEnabled({feature, country}) {

        let isEnabledWithoutQA = this.isFeatureEnabledWithoutQA({feature, country});
        let qaValue = this.getQAFeature({feature});

        if (!_.isNil(qaValue)) {
            return qaValue;
        }

        return isEnabledWithoutQA;
    }

    /**
     * Check if feature is disabled
     *
     * @param {{feature: {string}, country: {string}}}
     * @returns {boolean}
     */
    isFeatureDisabled({feature, country}) {
        let enabled = this.isFeatureEnabled({feature, country});

        return !_.isNil(enabled) ? !enabled : null;
    }


    /**
     * Prepare all features and their statuses data
     *
     * @param {{country: {string}}}
     * @returns {{items: [{}], enabledCount: {number}, disabledCount: {number}, testingCount: {number}}}
     */
    getFeaturesData({country}) {
        let items = {};

        _.each(this.getDefaultFeatures(), (value, key) => {
            items[key] = _.extend(items[key], {
                defaultValue: value,
                defaultValueString: value ? 'ON' : 'OFF',
                prodValue: value,
                resultingValue: value
            });
        });
        _.each(this.getRegionalFeatures({region: country}), (value, key) => {
            items[key] = _.extend(items[key], {
                regionValue: value,
                regionValueString: value ? 'ON' : 'OFF',
                prodValue: value,
                resultingValue: value
            });
        });
        _.each(this.getTokenFeatures(), (value, key) => {
            items[key] = _.extend(items[key], {
                tokenValue: value,
                tokenValueString: value ? 'ON' : 'OFF',
                prodValue: value,
                resultingValue: value
            });
        });

        _.each(this.getQAFeatures(), (value, key) => {
            items[key] = _.extend(items[key], {
                qaValue: value,
                qaValueDifferent: _.isNil(items[key]) ? true : value !== items[key].resultingValue,
                qaValueString: value ? 'ON' : 'OFF',
                resultingValue: value
            });
        });

        let stats = _.reduce(items, (result, value) => {
            if (value.resultingValue !== value.prodValue) {
                result.testing += 1;
            } else if (value.resultingValue === true) {
                result.enabled += 1;
            } else {
                result.disabled += 1;
            }
            return result;
        }, {enabled: 0, disabled: 0, testing: 0});

        return {
            items,
            enabledCount: stats.enabled,
            disabledCount: stats.disabled,
            testingCount: stats.testing
        };
    }

}



// WEBPACK FOOTER //
// ./shared/model/app-features/AppFeaturesModel.js